import {
    ActionIcon,
    Avatar,
    Badge,
    Button,
    Group,
    Loader,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AdminCapabilityEnum,
    AdminSectionEnum,
} from '@routellers/types/dist/admin/admin-permissions';
import { CityReadyRouteRO } from '@routellers/types/dist/city-ready-route/city-ready-route.ro';
import { ReadyRouteGuideTypeEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-guide-type.enum';
import { ReadyRouteStatusEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-status.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { IconMicrophone } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { RouteEditor } from '../editors/route-editor';
import { enumLabel } from '../i18n';
import { useAuth } from '../providers/auth-context';
import { adminCan } from '../utils/admin-permissions';
import { contentLanguagesForCountry } from '../utils/content-languages';
import { LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const STATUS_FILTER_ORDER = [
    ReadyRouteStatusEnum.NEED_REVIEW,
    ReadyRouteStatusEnum.DRAFT,
    ReadyRouteStatusEnum.REJECTED,
    ReadyRouteStatusEnum.PUBLISHED,
];

const statusFromSearch = (value: string | null): string => {
    if (value && Object.values(ReadyRouteStatusEnum).includes(value as ReadyRouteStatusEnum)) {
        return value;
    }
    return ALL;
};

const readyRouteLanguages = (row: CityReadyRouteRO): Set<Languages> =>
    new Set(
        (row.locales || [])
            .filter((locale) => !!locale.title?.trim())
            .map((locale) => locale.language),
    );

const voiceoverHref = (row: CityReadyRouteRO): string | null => {
    if (!row.citySlug?.trim() || !row.voiceoverSlug?.trim()) return null;

    const path = `/voiceover/routes/${encodeURIComponent(row.citySlug)}/${encodeURIComponent(row.voiceoverSlug)}`;
    // Prod admin nginx proxies /voiceover/ → API. Locally open API host directly.
    const onAdminHost =
        typeof window !== 'undefined' && window.location.hostname === 'admin.routellers.com';

    if (onAdminHost) return path;

    const api = (process.env.REACT_APP_API_DOMAIN || '').replace(/\/$/, '');

    return api ? `${api}${path}` : path;
};

export const RoutesPage = () => {
    const { t } = useTranslation();
    const { admin } = useAuth();
    const canEdit = adminCan(admin, AdminSectionEnum.ROUTES, AdminCapabilityEnum.EDIT);
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<CityReadyRouteRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>(() =>
        statusFromSearch(searchParams.get('status')),
    );
    const [cityFilter, setCityFilter] = useState('all');
    const [editorId, setEditorId] = useState<number | null>(() => {
        const id = Number(searchParams.get('edit'));
        return Number.isInteger(id) && id > 0 ? id : null;
    });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        const cityId = parseCityFilter(cityFilter);

        try {
            const where: Record<string, unknown> = {};
            if (cityId) where.cityId = cityId;
            if (statusFilter !== ALL) where.status = statusFilter;
            if (!Object.keys(where).length) where.id = null;

            const { data } = await api.cityReadyRoutesApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: search.trim() || undefined,
                where,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [cityFilter, statusFilter]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter, statusFilter, page]);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.routes.title')}
                action={
                    canEdit ? (
                        <Button
                            onClick={() => {
                                setEditorId(null);
                                setCreating(true);
                            }}
                        >
                            {t('pages.routes.create')}
                        </Button>
                    ) : undefined
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.routes.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setPage(1);
                            load();
                        }
                    }}
                    w={240}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(v) => {
                        setPage(1);
                        setCityFilter(v);
                    }}
                />
                <Select
                    w={180}
                    data={[
                        { value: ALL, label: t('pages.routes.statusAll') },
                        ...STATUS_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.readyRouteStatus', value),
                        })),
                    ]}
                    value={statusFilter}
                    onChange={(v) => {
                        setPage(1);
                        setStatusFilter(v || ALL);
                    }}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: 280, maxWidth: 280 }}>
                                        {t('pages.routes.columns.route')}
                                    </Table.Th>
                                    <Table.Th>{t('pages.routes.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.routes.columns.locales')}</Table.Th>
                                    <Table.Th>{t('pages.routes.columns.stops')}</Table.Th>
                                    <Table.Th>{t('pages.routes.columns.author')}</Table.Th>
                                    <Table.Th>{t('pages.routes.columns.status')}</Table.Th>
                                    <Table.Th style={{ width: 56 }} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => {
                                    const ready = readyRouteLanguages(row);
                                    const expected = contentLanguagesForCountry(row.country);
                                    const scriptHref = voiceoverHref(row);

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setEditorId(row.id)}
                                        >
                                            <Table.Td style={{ width: 280, maxWidth: 280 }}>
                                                <div style={{ maxWidth: 260, overflow: 'hidden' }}>
                                                    <EntityThumb
                                                        src={row.coverUrl}
                                                        name={row.title || `Route #${row.id}`}
                                                        subtitle={row.teaser || ''}
                                                        radius='md'
                                                    />
                                                </div>
                                            </Table.Td>
                                            <Table.Td>{row.cityTitle || `#${row.cityId}`}</Table.Td>
                                            <Table.Td>
                                                <Group gap={4} wrap='wrap'>
                                                    {expected.map((language) => {
                                                        const filled = ready.has(language);

                                                        return (
                                                            <Badge
                                                                key={language}
                                                                size='sm'
                                                                variant={
                                                                    filled ? 'filled' : 'light'
                                                                }
                                                                color={filled ? 'blue' : 'gray'}
                                                            >
                                                                {language.toUpperCase()}
                                                            </Badge>
                                                        );
                                                    })}
                                                    {!expected.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                {row.stopsCount ?? row.stops?.length ?? 0}
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap='xs' wrap='nowrap'>
                                                    <Avatar
                                                        src={row.authorAvatarUrl || undefined}
                                                        radius='xl'
                                                        size={24}
                                                    >
                                                        {(row.authorName || '?')
                                                            .slice(0, 1)
                                                            .toUpperCase()}
                                                    </Avatar>
                                                    <Text size='sm'>{row.authorName || '—'}</Text>
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={6}>
                                                    {row.guideType ? (
                                                        <Badge
                                                            color={
                                                                row.guideType ===
                                                                ReadyRouteGuideTypeEnum.SCENIC
                                                                    ? 'teal'
                                                                    : row.guideType ===
                                                                        ReadyRouteGuideTypeEnum.HISTORICAL
                                                                      ? 'grape'
                                                                      : 'yellow'
                                                            }
                                                            variant='light'
                                                        >
                                                            {enumLabel(
                                                                t,
                                                                'enums.readyRouteGuideType',
                                                                row.guideType,
                                                            )}
                                                        </Badge>
                                                    ) : null}
                                                    <Badge
                                                        color={
                                                            row.status ===
                                                            ReadyRouteStatusEnum.PUBLISHED
                                                                ? 'green'
                                                                : row.status ===
                                                                    ReadyRouteStatusEnum.NEED_REVIEW
                                                                  ? 'yellow'
                                                                  : row.status ===
                                                                      ReadyRouteStatusEnum.REJECTED
                                                                    ? 'red'
                                                                    : 'orange'
                                                        }
                                                        variant='light'
                                                    >
                                                        {enumLabel(
                                                            t,
                                                            'enums.readyRouteStatus',
                                                            row.status,
                                                        )}
                                                    </Badge>
                                                </Group>
                                            </Table.Td>
                                            <Table.Td onClick={(e) => e.stopPropagation()}>
                                                {scriptHref ? (
                                                    <Tooltip label={t('pages.routes.openVoiceover')}>
                                                        <ActionIcon
                                                            variant='subtle'
                                                            color='gray'
                                                            component='a'
                                                            href={scriptHref}
                                                            target='_blank'
                                                            rel='noopener noreferrer nofollow'
                                                            aria-label={t('pages.routes.openVoiceover')}
                                                        >
                                                            <IconMicrophone size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : (
                                                    <Text size='xs' c='dimmed'>
                                                        —
                                                    </Text>
                                                )}
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.routes.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <RouteEditor
                opened={creating || editorId != null}
                routeId={creating ? null : editorId}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={load}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
                initialCityId={parseCityFilter(cityFilter)}
            />
        </Stack>
    );
};
